import { defineStore } from 'pinia'
import { watch, onScopeDispose } from 'vue'
import SyncService from '@/services/SyncService'
import type { SyncType, SyncStatusType } from '@/types'
import { requireSync, hamrsDotAppURL } from '@/config'
import { useToastSystem } from '@/composables/useToastSystem'
import { useLoggerStore } from '@/stores/LoggerStore'
import { useSettingStore } from '@/stores/SettingStore'
import { hamrsSyncLogger } from '@/utils/loggers'
import { useLocalStorage } from '@vueuse/core'

const { syncErrorToast } = useToastSystem()

export const useSyncStore = defineStore('sync', {
  state: () => ({
    sync: {} as SyncType,
    status: {} as SyncStatusType,
    onlineWatcherSetup: false,
    liveSyncActive: useLocalStorage("pinia/sync/liveSyncActive", false),
    onlineWatcherUnwatch: null as (() => void) | null,
    initialLiveSyncSetup: false
  }),
  getters: {
    canLiveSync() {
      // Must have an API key
      if (!this.sync?.hamrs?.apiKey || this.sync.hamrs.apiKey === '') {
        return false
      }
      // Must have valid status (API key works) - only disable if explicitly invalid
      if (this.status?.valid === false) {
        return false
      }
      // Must be online for live sync to work
      const settingStore = useSettingStore()
      if (!settingStore.isOnline) {
        return false
      }
      return true
    }
  },
  actions: {
    async load() {
      try {
        const sync = await SyncService.find()
        this.sync = sync

        // Configure sync - status will be determined in callback
        SyncService.configureSync(sync, this.syncComplete)

        // Watch for online status changes to manage live sync (only setup once)
        if (!this.onlineWatcherSetup) {
          this.setupOnlineStatusWatcher()
          this.onlineWatcherSetup = true
        }
      } catch (error) {
        hamrsSyncLogger('Error fetching sync:', error)
      }
    },

    setupOnlineStatusWatcher() {
      const settingStore = useSettingStore()

      // Store the unwatch function to prevent memory leaks
      this.onlineWatcherUnwatch = watch(
        () => settingStore.isOnline,
        (isOnline) => {
          if (this.liveSyncActive) {
            if (isOnline) {
              // User came back online and wants live sync - reconfigure and enable if possible
              SyncService.configureSync(this.sync, (status) => {
                this.syncComplete(status)
                if (this.canLiveSync) {
                  this.enableLiveSync()
                }
              })
            } else {
              // User went offline - disable live sync (but keep preference)
              this.disableLiveSync()
            }
          }
        }
      )

      // Clean up watcher when store scope is disposed
      onScopeDispose(() => {
        if (this.onlineWatcherUnwatch) {
          this.onlineWatcherUnwatch()
          this.onlineWatcherUnwatch = null
        }
      })
    },

    async create(token: string) {
      const data = {
        hamrs: {
          apiKey: token
        }
      } as SyncType
      return this.update(data)
    },

    async update(newSyncData: SyncType) {
      try {
        const createdSync = await SyncService.update(newSyncData)
        await this.load()
        return createdSync
      } catch (error) {
        hamrsSyncLogger('Error updating sync:', error)
        throw error
      }
    },

    // syncComplete gets called as a callback inside the SyncService whenever
    // replication catches up. This allows us to show the "last synced" time on
    // the SyncType page and other actions can be triggered off this event using
    // syncStore.$onAction
    async syncComplete(status: SyncStatusType) {
      this.status = status
      if (!status.valid) {
        syncErrorToast(status.error)
      }

      // Handle live sync initialization after sync status is determined (only once)
      if (!this.initialLiveSyncSetup) {
        this.initialLiveSyncSetup = true

        if (this.liveSyncActive && this.canLiveSync) {
          // User wants live sync and conditions allow - start it
          this.enableLiveSync()
          hamrsSyncLogger('Live sync restored from previous session')
        } else if (this.liveSyncActive) {
          // User wants live sync but conditions don't allow - keep preference but don't start
          hamrsSyncLogger('Live sync preference saved, but not started - conditions not met (offline, no API key, or invalid key)')
        }
      }
    },

    async cancel() {
      // This is dangerous to do outside of the web flow so we have an extra
      // guard here.
      if (!requireSync) {
        return
      }
      try {
        await SyncService.cancelSyncAndDestroy()
        window.location.href = `${hamrsDotAppURL}/?sign_out=true`
      } catch (error) {
        hamrsSyncLogger('Error fetching sync:', error)
      }
    },

    enableLiveSync() {
      // Only enable if conditions are met
      if (!this.canLiveSync) {
        return
      }

      SyncService.enableLiveSync((change: any) => {
        this.handleLiveSyncChange(change)
      })

      // Refresh current logbook to pick up any changes that happened while live sync was off
      const loggerStore = useLoggerStore()
      if (loggerStore.currentLogbook?._id) {
        loggerStore.fetchQsoList(loggerStore.currentLogbook._id)
        hamrsSyncLogger('Refreshing QSOs after enabling live sync')
      } else {
        hamrsSyncLogger('No current logbook found after enabling live sync')
      }

      hamrsSyncLogger('Live sync enabled')
    },

    disableLiveSync() {
      SyncService.disableLiveSync()
      hamrsSyncLogger('Live sync disabled')
    },

    handleLiveSyncChange(change: any) {
      const { doc } = change
      const loggerStore = useLoggerStore()

      if (doc._id.startsWith('LOGBOOK:')) {
        // Logbook changed - refresh QSO data for current logbook
        if (loggerStore.currentLogbook?._id === doc._id) {
          hamrsSyncLogger('Refreshing QSOs due to logbook change')
          try {
            // Pass the full logbook ID (LOGBOOK:uuid) to match normal app behavior
            loggerStore.fetchQsoList(doc._id)
          } catch (error) {
            hamrsSyncLogger('Failed to refresh QSOs during live sync:', error)
            // Optionally notify user of refresh failure
          }
        }
      }
    },

    toggleLiveSync() {
      // Simply toggle the preference/state
      this.liveSyncActive = !this.liveSyncActive

      if (this.liveSyncActive && this.canLiveSync) {
        this.enableLiveSync()
      } else {
        this.disableLiveSync()
      }
    }
  }
})
